/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { v4 as uuidv4 } from "uuid";

import { BlockSnapshot } from "@vienna/common/src";

import { BlockFactory } from "../../../block/BlockFactory";
import { MathUtils } from "../../../utilities/MathUtils";
import { PositionUtils } from "../../../utilities/PositionUtils";
import { Plate } from "../../Plate";
import { BaseSession } from "../BaseSession";
import { MoveCommand } from "src/classes/plate/Command";
import { Key } from "src/classes/plate/Keyboard";
import { DragBlockSessionSnapshot } from "src/classes/plate/Session";
import { SessionType } from "src/classes/plate/Session/session.types";

export class DragBlockSession extends BaseSession<DragBlockSessionSnapshot> {
  readonly initialBlockSnapshots: BlockSnapshot[];

  readonly plate: Plate;

  private constructor(snapshot: DragBlockSessionSnapshot, plate: Plate) {
    super(snapshot);
    this.plate = plate;
    this.initialBlockSnapshots = this.plate.selectedBlocks.map((block) => block.snapshot);

    this.onDragBlock = this.onDragBlock.bind(this);
  }

  public start(): void {
    document.addEventListener("mousemove", this.onDragBlock);
  }

  public end(): void {
    document.removeEventListener("mousemove", this.onDragBlock);
    // if (!this.dragSession?.initialBlockPositions.length) return;
    const cmd = new MoveCommand(this, this.plate);
    if (cmd.blocksMoved()) {
      this.plate.addToHistory(cmd);
    }
  }

  public cancel(): void {
    this.end();
  }

  private onDragBlock(): void {
    const isDuplicating = this.plate.keyboardManager.isKeyPressed(Key.Alt);
    const isLockedToAxis = this.plate.keyboardManager.isKeyPressed(Key.Shift);
    if (isDuplicating && !this.snapshot.hasDuplicated) {
      this.duplicateOnDrag(); // if pressing alt, duplicate the block(s). only do this once per drag session.
    }

    const maxMove = PositionUtils.getMaxMoveForBlocks(
      this.plate.selectedBlocks.map((b) => b.snapshot),
      {
        width: this.plate.snapshot.width,
        height: this.plate.snapshot.height,
      }
    );

    const { dx, dy } = MathUtils.getDistanceBetweenPoints(
      this.plate.mouseTracker.snapshot.position,
      this.snapshot.anchor,
      isLockedToAxis
    );

    this.plate.selectedBlocks.forEach((block) => {
      const initialBlock = this.initialBlockSnapshots.find(({ id }) => id === block.snapshot.id);
      if (!initialBlock) throw new Error("Could not find block to move.");

      const naiveTop = initialBlock.top + dy;
      const minTop = block.snapshot.top - maxMove.up;
      const maxTop = block.snapshot.top + maxMove.down;
      const top = MathUtils.constrainValueToBounds(naiveTop, minTop, maxTop);

      const naiveLeft = initialBlock.left + dx;
      const minLeft = block.snapshot.left - maxMove.left;
      const maxLeft = block.snapshot.left + maxMove.right;
      const left = MathUtils.constrainValueToBounds(naiveLeft, minLeft, maxLeft);

      block.move(top, left);
    });
  }

  /**
   * Duplicates the selected blocks on alt+drag
   */
  private duplicateOnDrag(): void {
    const duplicatedSnapshots = this.plate.selectedBlocks.map(({ snapshot }) => ({
      ...snapshot,
      id: uuidv4(),
      zIndex: this.plate.highestZIndex.value - 1,
    }));
    const blocks = duplicatedSnapshots.map((snapshot) => BlockFactory.create(snapshot, this.plate.socket));
    this.plate.createBlocks(blocks);
    this.setState({ hasDuplicated: true });
  }

  /* -------------------------------------------------- */
  /*                   Static Methods                   */
  /* -------------------------------------------------- */
  public static create(plate: Plate): DragBlockSession {
    const snapshot: DragBlockSessionSnapshot = {
      type: SessionType.DragBlock,
      anchor: { x: plate.mouseTracker.snapshot.position.x, y: plate.mouseTracker.snapshot.position.y },
      hasDuplicated: false,
    };
    return new DragBlockSession(snapshot, plate);
  }
}
